import React from 'react';
import { money } from './earnings';

// #region ResultPanel
/**
 * The Royal Azure answer card. The monthly figure leads, since that is what lands in
 * the artist's account; the year and the per-order working sit under it.
 */
export const ResultPanel: React.FunctionComponent<React.PropsWithChildren<{
    label: string;
    perOrder: number;
    ordersPerMonth: number;
}>> = ({ label, perOrder, ordersPerMonth, children }): React.ReactElement => {
    const monthly = Math.round(perOrder * ordersPerMonth * 100) / 100;
    return (
        <div className="relative isolate flex flex-col overflow-hidden rounded-2xl bg-blue-600 p-5 text-white shadow-lg shadow-blue-900/15 sm:p-6">
            <WallpaperMotif />
            <p className="text-sm font-medium text-white/90">{label}</p>
            <p className="mt-1 flex flex-wrap items-baseline gap-x-2" aria-live="polite">
                <span className="break-all font-display text-5xl font-semibold tracking-tight tabular-nums sm:text-6xl">{money(monthly, monthly < 1000)}</span>
                <span className="whitespace-nowrap text-sm text-white/80">a month</span>
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-sm font-medium tabular-nums">
                <span className="rounded-lg bg-brand px-3 py-1.5 font-semibold text-black shadow-sm">{money(monthly * 12, false)} a year</span>
                <span className="rounded-lg bg-white/15 px-3 py-1.5 ring-1 ring-inset ring-white/20">
                    {money(perOrder)} × {ordersPerMonth} {ordersPerMonth === 1 ? 'order' : 'orders'}
                </span>
            </div>
            <p className="mt-6 border-t border-white/20 pt-4 text-xs font-semibold uppercase tracking-wider text-white/70">Each order</p>
            <dl className="mt-2 space-y-2 text-sm tabular-nums">{children}</dl>
        </div>
    );
};

// #region ResultRow
export const ResultRow: React.FunctionComponent<{ term: string; value: string; muted?: boolean; strong?: boolean }> = ({ term, value, muted, strong }): React.ReactElement => (
    <div className={`flex items-start justify-between gap-4 ${strong ? 'border-t border-white/20 pt-2 font-semibold' : ''}`}>
        <dt className={muted ? 'text-white/75' : 'text-white'}>{term}</dt>
        <dd className="shrink-0">{value}</dd>
    </div>
);

// A quiet repeat pattern in the corner: a nod to wallpaper, not a logo.
const WallpaperMotif: React.FunctionComponent = (): React.ReactElement => (
    <svg aria-hidden className="pointer-events-none absolute -right-10 -top-10 -z-10 h-56 w-56 text-white/10" viewBox="0 0 120 120">
        <defs>
            <pattern id="pricing-motif" width="24" height="24" patternUnits="userSpaceOnUse">
                <path d="M12 2 L22 12 L12 22 L2 12 Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <circle cx="12" cy="12" r="2.5" fill="currentColor" />
            </pattern>
        </defs>
        <circle cx="60" cy="60" r="60" fill="url(#pricing-motif)" />
    </svg>
);
